import {
  CollectionType,
  Common,
  CoreDoms,
  EditorExperienceType,
  PreviewAPI,
  ViewMode,
  Viewers,
} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import classNames from 'classnames';
import React, {CSSProperties, useEffect, useMemo, useRef, useState} from 'react';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {
  HelpTip,
  Horizontal,
  PlusIcon,
  Theme,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {getPageName} from '../../Utils/Common';
import AddPagesButton from '../Page/Actions/AddPagesButton';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useSortable} from '@dnd-kit/sortable';
// eslint-disable-next-line node/no-extraneous-import
import {CSS} from '@dnd-kit/utilities';
import {useExperience} from '../Experience/ExperienceProvider';
import {useTranslation} from 'react-i18next';

export type ThumbnailProps = {
  page: PageInterface;
  layout: ProductLayout;
  order?: number;
  onChangePage: (pageNumber: number) => void;
};

const Thumbnail = ({page, layout, order, onChangePage}: ThumbnailProps) => {
  const ui = useUiState();
  const ref = useRef<PreviewAPI<any> | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [sortableId, setSortableId] = useState<number | undefined>(page.id);
  const [showLockedTip, setShowLockedTip] = useState<boolean>(false);
  const {experience} = useExperience();

  const [sortDisabled, setSortDisabled] = useState(
    !experience.EDITOR.workArea.singlePage.pageThumbnailRearrangePage
  );
  const {t} = useTranslation();

  const {attributes, listeners, setNodeRef, transform, isDragging} = useSortable({
    id: sortableId as number,
    disabled: sortDisabled,
    data: {
      api: ref.current,
    },
  });

  const id = `${CoreDoms.ViewportName}-${Viewers.Secondary}-${page.pagenumber}`;
  const selected = ui.state.pageNumber === page.pagenumber;

  const settings = useMemo(() => {
    return {
      viewerName: Viewers.Secondary,
      ismobile: false,
      isCanvasListening: false,
      isCalendarEvent: false,
      singlePageThumbnail: false,
      autoLayoutEnabled: false,
      zoom: 100,
      pageNumber: page.pagenumber,
      cache: true,
      editorType: EditorExperienceType.Thumbnail,
      components: {
        background: true,
        bleed: {
          outside: false,
          inside: false,
        },
        gutter: true,
        spine: true,
        flaps: true,
        empty: true,
        wrap: true,
        errors: false,
      },
    };
  }, []);

  // create the API instance
  useEffect(() => {
    let api: PreviewAPI<any> = null as any;
    if (!ref.current) {
      api = new PreviewAPI(settings);
      ref.current = api;
    }

    api
      .setSpreadSize({
        pageNumber: page.pagenumber,
        rulersVisible: false,
        rulerOriginHeight: 0,
        borderSize: 0,
        left: 0,
        top: 0,
        viewMode: ViewMode.List,
      })
      .display()
      .then(() => {
        if (
          !experience.EDITOR.workArea.singlePage.pageThumbnailRearrangePage ||
          api.insideLeft() ||
          api.outsideRight() ||
          Common.isPageCover(page.pagenumber)
        ) {
          setSortDisabled(true);
          // setting the id to undefined will prevent disabled thumbnails from being shifted
          setSortableId(undefined);
        } else {
          setSortDisabled(false);
          setSortableId(page.id);
        }
      });

    return () => {
      api.destroy();

      // we may not have a stage if the API is not in the viewport
      if (api.getStage()) {
        api.setStageSize({width: 0, height: 0});
      }
      ref.current = null;
    };
  }, [page.ref, page.id, layout.pagecount]);

  const changePage = (event: React.MouseEvent): void => {
    onChangePage(page.pagenumber);
  };

  const listItemClasses = classNames([
    'flex',
    'items-center',
    'cursor-pointer',
    'selected:bg-red-500',
    'hoverAncestor',
    'snap-center',
    'group',
    'mx-sm',
    'first:ml-0',
    isDragging && !sortDisabled && 'dragging',
  ]);

  const pageContainerClasses = classNames([
    'relative',
    'flex',
    'flex-col',
    'justify-center',
    'sm:snap-center',
    !sortDisabled && 'pointer-events-none',
  ]);

  const canvasContainerClasses = classNames(
    'shadow-[0_2px_2px_rgb(0_0_0_/_15%)]',
    selected &&
      'outline outline-themeAccentColour rounded-xxs outline-[2rem] outline-offset-[1rem]',
    // Add styling to the item when it is being dragged
    'group-[.dragging]:shadow-[0_10px_10px_rgb(0_0_0_/_10%)]',
    'group-[.dragging]:rounded-xs',
    'group-[.dragging]:outline',
    'group-[.dragging]:outline-[3rem]',
    'group-[.dragging]:outline-offset-[-3rem]',
    'group-[.dragging]:outline-themeAccentColour',
    'group-[.dragging]:translate-y-[-10rem]'
  );

  const pageNameClasses = classNames(
    'flex',
    'justify-center',
    'self-center',
    'px-sm',
    'py-xxs',
    'rounded-full',
    'text-center',
    'text-sm',
    'leading-[1]',
    {'opacity-40': !selected},
    'ancestorHover:opacity-100',
    'overflow-hidden'
  );

  useEffect(() => {
    if (selected) {
      /*
      NOTE: Do not use behaviour 'smooth' on scrollIntoView because it triggers browser bugs that prevents
      two project viewers scrolling at the same time (i.e. primary and thumbnails),
      and/or scrolls to the wrong location
      */
      canvasRef.current?.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }
  }, [ui.state.pageNumber, selected]);

  const addPagesButtonProps: ButtonProps = {
    hideLabel: true,
    size: 'small',
    corners: 'round',
    buttonStyle: 'tertiary',
    startIcon: <PlusIcon />,
    className: 'mb-lg self-center w-lg h-lg',
  };

  // Define some prop overrides for the add pages button popout so it displays in the correct place when appearing in the thumbnail bar
  const addPagesPopoutProps: Partial<PopOutProps> = {
    // On small screen, the add pages popout is anchored to the centre of the thumbnail bar to ensure centred with the clicked button
    ...(ui.state.isSmallScreen && {anchorId: 'projectViewerThumbs', offset: {x: 0, y: 25}}),
  };

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
  };

  if (isDragging) {
    style.zIndex = 1;
  }

  return (
    <>
      <li
        id={`page-${page.pagenumber}`}
        data-id={page.pagenumber}
        className={listItemClasses}
        onClick={changePage}
        ref={setNodeRef}
        style={{...style, order: order}}
        {...(!sortDisabled && attributes)}
        {...(!sortDisabled && listeners)}
        {...(sortDisabled && {
          draggable: experience.EDITOR.workArea.singlePage.pageThumbnailRearrangePage,
          onDragStart: event => {
            event.preventDefault();
            // only show the tip if we're permitted to drag
            if (experience.EDITOR.workArea.singlePage.pageThumbnailRearrangePage) {
              setShowLockedTip(true);
            }
          },
          onContextMenu: event => {
            event.preventDefault();
          },
        })}
      >
        <div className={pageContainerClasses}>
          <div ref={canvasRef} id={id} className={canvasContainerClasses}></div>
          <div className={'flex h-[32rem] justify-center mt-xxs'}>
            <Theme
              name={selected ? ThemeName.Primary : undefined}
              allowBorder={false}
              element={'p'}
              className={pageNameClasses}
            >
              <span className={'break-words line-clamp overflow-ellipsis'}>
                {getPageName(page, layout, layout.type === CollectionType.Calendar)}
              </span>
            </Theme>
          </div>
        </div>
        {sortDisabled && (
          <HelpTip
            open={showLockedTip}
            anchorId={`page-${page.pagenumber}`}
            message={t('*:str_MessageThisPageCannotBeMoved')}
            hideAfter={2000}
            onClose={() => setShowLockedTip(false)}
            anchorOrigin={{horizontal: Horizontal.Centre, vertical: Vertical.Top}}
            transformOrigin={{horizontal: Horizontal.Centre, vertical: Vertical.Bottom}}
            arrow
            popDirection="up"
            edgeBehaviour="move"
            className={'max-w-[240rem]'}
          />
        )}
      </li>
      {experience.EDITOR.workArea.singlePage.pageThumbnailAddPages &&
        !Common.isBackCover(page.pagenumber) &&
        (!Common.isPageCover(page.pagenumber) || 1 !== layout.firstpagenumber) &&
        layout.minpages !== layout.maxpages && (
          <li id={`add-page-after-${page.pagenumber}`} className={'flex'} style={{order: order}}>
            <AddPagesButton
              buttonProps={addPagesButtonProps}
              pageNumber={page.pagenumber}
              popoutProps={addPagesPopoutProps}
            />
          </li>
        )}
    </>
  );
};

export default Thumbnail;
